import { axiosJSONInstance } from '@/config';
import {
  ProductCatalogItem,
  ProductCatalogResponse,
  ProductCatalogSingleResponse,
  ProductCatalogRequest,
  ProductCatalogImportPreviewResponse,
  ProductCatalogImportCommitResponse,
} from '@/types/productCatalog';

// Fetch products (paginated, with client-side search via /search endpoint)
export const fetchProductCatalog = async (
  page = 1,
  perPage = 20,
  search?: string,
  category?: string,
): Promise<ProductCatalogResponse> => {
  // When searching, use the /search endpoint (backend index doesn't support search)
  if (search) {
    const params = new URLSearchParams({ q: search });
    if (category) params.append('category', category);

    const response = await axiosJSONInstance.get(`/merchants/product-catalog/search?${params.toString()}`);
    const items: ProductCatalogItem[] = Array.isArray(response.data?.object) ? response.data.object : [];
    return {
      status: response.data?.status ?? 'success',
      code: response.data?.code ?? 200,
      message: response.data?.message ?? '',
      object: {
        items,
        page: 1,
        total: items.length,
        pages: 1,
        perPage: items.length,
      },
    };
  }

  const params = new URLSearchParams({
    page: page.toString(),
    per_page: perPage.toString(),
  });
  if (category) params.append('category', category);

  const response = await axiosJSONInstance.get(`/merchants/product-catalog?${params.toString()}`);
  return response.data;
};

// Search products (autocomplete, max 10)
export const searchProductCatalog = async (
  search: string,
  category?: string,
): Promise<{ status: string; object: ProductCatalogItem[] }> => {
  const params = new URLSearchParams({ q: search });
  if (category) params.append('category', category);

  const response = await axiosJSONInstance.get(`/merchants/product-catalog/search?${params.toString()}`);
  return response.data;
};

// Create product
export const createProduct = async (
  data: ProductCatalogRequest,
): Promise<ProductCatalogSingleResponse> => {
  const response = await axiosJSONInstance.post('/merchants/product-catalog', data);
  return response.data;
};

// Update product
export const updateProduct = async (
  uid: string,
  data: Partial<ProductCatalogRequest>,
): Promise<ProductCatalogSingleResponse> => {
  const response = await axiosJSONInstance.patch(`/merchants/product-catalog/${uid}`, data);
  return response.data;
};

// Delete product
export const deleteProduct = async (uid: string): Promise<void> => {
  await axiosJSONInstance.delete(`/merchants/product-catalog/${uid}`);
};

// Download CSV template
export const fetchProductImportTemplate = async (): Promise<Blob> => {
  const response = await axiosJSONInstance.get('/merchants/product-catalog/import/template', {
    responseType: 'blob',
  });
  return response.data;
};

// Detect CSV columns
export const detectProductImportColumns = async (
  file: File,
): Promise<{ status: string; object: { columns: string[] } }> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axiosJSONInstance.post('/merchants/product-catalog/import/detect-columns', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

// Preview CSV import
export const previewProductImport = async (
  file: File,
): Promise<ProductCatalogImportPreviewResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axiosJSONInstance.post('/merchants/product-catalog/import/preview', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

// Commit CSV import
export const commitProductImport = async (
  file: File,
): Promise<ProductCatalogImportCommitResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axiosJSONInstance.post('/merchants/product-catalog/import/commit', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};
